import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { IssueSupplier, IssueUpdateDataSupplier, QuproServiceError, IssuesFilterSupplier, BaseUser } from '@arpada/arp-lib-common-qupro';
import { PortalProveedorService } from '../../../services/api.services';
import { ModeloError, EdicionMasivaModel } from '../../../models/api.models';
import { IssuesInfoComponent } from './issues-info/issues-info.component';
import { from, of } from 'rxjs';
import { concatMap, map, catchError, retryWhen, delay, take } from 'rxjs/operators';

@Component({
  selector: 'app-edicion-masiva',
  templateUrl: './edicion-masiva.component.html',
  styleUrls: ['./edicion-masiva.component.scss'],
})
export class EdicionMasivaComponent implements OnInit {

  @Input() issuesToEdit: IssueSupplier[];
  @Input() isModal: boolean;

  appointmentDate: string;
  materialDate: string;
  repairDate: string;
  commentToSend: string;
  modeloList: EdicionMasivaModel[] = [];
  issuesNotEditable: IssueSupplier[] = [];
  issuesNotEditableAppDate: IssueSupplier[] = [];
  issuesNotEditableMaterialDate: IssueSupplier[] = [];
  issuesNotEditableRepairDate: IssueSupplier[] = [];
  issuesNotEditableSupplierComment: IssueSupplier[] = [];
  showInfo = false;
  issuesFilter: IssuesFilterSupplier = new IssuesFilterSupplier();
  supplierUser: BaseUser = new BaseUser();

  constructor(private modalCtrl: ModalController,
              private portalProveedorService: PortalProveedorService) { }

  ngOnInit() {
    this.portalProveedorService.getSupplierData().subscribe((baseUser: BaseUser) => {
      this.supplierUser = baseUser;
    });
    this.issuesToEdit.forEach((issueToEdit: IssueSupplier) => {
      if (!issueToEdit.supplier_actions.includes('is_editable')) {
        this.issuesNotEditable.push(issueToEdit);
      }
      if (!issueToEdit.supplier_actions.includes('can_modify_appointment_date')) {
        this.issuesNotEditableAppDate.push(issueToEdit);
      }
      if (!issueToEdit.supplier_actions.includes('can_modify_material_date')) {
        this.issuesNotEditableMaterialDate.push(issueToEdit);
      }
      if (!issueToEdit.supplier_actions.includes('can_modify_repair_date')) {
        this.issuesNotEditableRepairDate.push(issueToEdit);
      }
      if (!issueToEdit.supplier_actions.includes('can_add_supplier_comment')) {
        this.issuesNotEditableSupplierComment.push(issueToEdit);
      }
    });
  }

  guardar() {
    this.modeloList = [];
    this.issuesToEdit.forEach((issueToEdit: IssueSupplier) => {
      const modelo: EdicionMasivaModel =
        this.createModelo(this.appointmentDate, this.materialDate, this.repairDate, this.commentToSend, issueToEdit);
      this.modeloList.push(modelo);
    });
    this.presentIssueInfo(this.modeloList);
  }

  async presentIssueInfo(modeloList: EdicionMasivaModel[]) {
    const modalCtrl = await this.modalCtrl.create({
      component: IssuesInfoComponent,
      componentProps: {
        issuesNotEditable: this.issuesNotEditable,
        issuesNotEditableAppDate: this.issuesNotEditableAppDate,
        issuesNotEditableMaterialDate: this.issuesNotEditableMaterialDate,
        issuesNotEditableRepairDate: this.issuesNotEditableRepairDate,
        issuesNotEditableSupplierComment: this.issuesNotEditableSupplierComment
      }
    });
    await modalCtrl.present();
    modalCtrl.onDidDismiss().then(data => {
      if (data.data) {
        this.updateIssues(modeloList);
      }
    });
  }

  updateIssues(modeloList: EdicionMasivaModel[]) {
    from(modeloList).pipe(
      concatMap(modelo =>
        this.portalProveedorService.updateIssue(modelo.id, modelo.issueUpdateDataSupplier).pipe(
          retryWhen(errors => errors.pipe(delay(500), take(2))),
          map(() => ({ success: true as const, id: modelo.id })),
          catchError((error: QuproServiceError) => of({ success: false as const, id: modelo.id, error }))
        )
      )
    ).subscribe((result: { success: boolean; id: number; error?: QuproServiceError }) => {
      if (result.success) {
        this.portalProveedorService.edicionMasivaEvent.emit(result.id);
      } else {
        const modeloError: ModeloError = new ModeloError();
        modeloError.id = result.id;
        modeloError.error = result.error ? result.error.message : 'Error desconocido';
        this.portalProveedorService.errorEdicionMasivaEvent.emit(modeloError);
      }
    });
  }

  private createModelo(appointmentDate: string, materialDate: string, repairDate: string,
                       commentToSend: string, issueToEdit: IssueSupplier): EdicionMasivaModel {
      const modelo: EdicionMasivaModel = new EdicionMasivaModel();
      const issueUpdateDataSupplier: IssueUpdateDataSupplier = new IssueUpdateDataSupplier();
      modelo.id = issueToEdit.id;
      if (issueToEdit.supplier_actions.includes('is_editable')) {
        if (issueToEdit.supplier_actions.includes('can_modify_appointment_date') && appointmentDate) {
          issueUpdateDataSupplier.appointment_date = new Date(this.appointmentDate).getTime().toString();
        }
        if (issueToEdit.supplier_actions.includes('can_modify_material_date') && materialDate) {
          issueUpdateDataSupplier.material_date = new Date(this.materialDate).getTime().toString();
          issueUpdateDataSupplier.material = this.setMaterial(materialDate, issueToEdit);
        }
        if (issueToEdit.supplier_actions.includes('can_modify_repair_date') && repairDate) {
          issueUpdateDataSupplier.repair_date = new Date(this.repairDate).getTime().toString();

        }
        if (issueToEdit.supplier_actions.includes('can_add_supplier_comment') && commentToSend) {
          issueUpdateDataSupplier.add_comment_supplier = this.commentToSend;
          issueUpdateDataSupplier.add_comment_supplier_name = this.supplierUser.firstname + ' ' + this.supplierUser.lastname;
        }
        modelo.issueUpdateDataSupplier = issueUpdateDataSupplier;
      }
      return modelo;
  }

  private setMaterial(materialDate: string, issueToEdit: IssueSupplier): number {
    let material: number; // por defecto esto es undefined
    // conversion de fechas para comparar
    const fechaAnterior = issueToEdit.material_date;
    let fechaActual = '';
    if (materialDate !== '' && materialDate !== null && materialDate !== undefined) {
      fechaActual = new Date(materialDate).getTime().toString();
    }
    if ((fechaAnterior !== null || fechaAnterior !== '') && (fechaActual === null || fechaActual === '')) {
      return material = 0;
    }
    if ((fechaAnterior === null  || fechaAnterior === '') && (fechaActual !== null || fechaActual !== '')) {
      return material = 1;
    }
    if ((fechaAnterior !== null || fechaAnterior !== '') &&
        (fechaActual !== null || fechaActual !== '') && (fechaActual !== fechaAnterior)) {
          return material = undefined;
    }
  }

  exit() {
    this.modalCtrl.dismiss();
  }

  clearDate(date: string, eventInfo) {
    if (date === 'appointmentDate') {
      this.appointmentDate = '';
    }
    if (date === 'materialDate') {
      this.materialDate = '';
    }
    if (date === 'repairDate') {
      this.repairDate = '';
    }
  }

  showHelp() {
    this.showInfo = !this.showInfo;
  }
}
